import { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useExpenseByIdQuery } from '../../../../__generated__/graphql';
import { collectFormDataExtraFields } from '../../../../components/ExtraFields/logic';
import {
  mapExpenseToFormData,
  findTravelExpenseItem,
} from '../carMileageHelpers';
import { MileageFormData } from '../carMileageHelpers';

type UseCarMileageFormInitializationProps = {
  expenseId: string | undefined;
  hasLoadedInitialData: boolean;
  setFormData: (data: MileageFormData) => void;
  setHasLoadedInitialData: (value: boolean) => void;
  setTravelExpenseItemId: (id: string | null) => void;
  setAddressFrom: (address: string | null | undefined) => void;
  setAddressTo: (address: string | null | undefined) => void;
  setKmDistance: (distance: number | null) => void;
  setDistanceSyncInProgress: (inProgress: boolean) => void;
  geocodeAddress: (address: string, type: 'start' | 'end') => void;
  lastSavedDataRef: React.MutableRefObject<{
    formData: MileageFormData;
    extraFieldsString: string;
  } | null>;
};

export const useCarMileageFormInitialization = ({
  expenseId,
  hasLoadedInitialData,
  setFormData,
  setHasLoadedInitialData,
  setTravelExpenseItemId,
  setAddressFrom,
  setAddressTo,
  setKmDistance,
  setDistanceSyncInProgress,
  geocodeAddress,
  lastSavedDataRef,
}: UseCarMileageFormInitializationProps) => {
  const { data: expenseData } = useExpenseByIdQuery({
    variables: { id: expenseId || '' },
    skip: !expenseId,
  });

  const { watch } = useFormContext<Record<string, unknown>>();
  const isFirstRenderAfterLoadRef = useRef<boolean>(true);
  const previousExtraFieldsRef = useRef<string>('');

  useEffect(() => {
    if (expenseData?.expense && !hasLoadedInitialData) {
      const expense = expenseData.expense;
      const initialData = mapExpenseToFormData(expense);
      setFormData(initialData);

      const travelItem = findTravelExpenseItem(expense.expenseItems);

      if (travelItem) {
        setTravelExpenseItemId(travelItem.Id);

        if (travelItem.flair__Address_From__c) {
          const address = travelItem.flair__Address_From__c;
          setAddressFrom(address || '');
          geocodeAddress(address, 'start');
        }

        if (travelItem.flair__Address_To__c) {
          const address = travelItem.flair__Address_To__c;
          setAddressTo(address || '');
          geocodeAddress(address, 'end');
        }

        if (travelItem.flair__Km_Distance__c) {
          setKmDistance(travelItem.flair__Km_Distance__c);
        }

        setDistanceSyncInProgress(
          travelItem.flair__Distance_Sync_In_Progress__c || false,
        );
      }

      const allFormValues = watch();
      const initialExtraFields = collectFormDataExtraFields(
        allFormValues || {},
      );
      const initialExtraFieldsString = JSON.stringify(initialExtraFields);

      lastSavedDataRef.current = {
        formData: initialData,
        extraFieldsString: initialExtraFieldsString,
      };

      setHasLoadedInitialData(true);
    }
  }, [
    expenseData,
    hasLoadedInitialData,
    watch,
    lastSavedDataRef,
    setFormData,
    setTravelExpenseItemId,
    setAddressFrom,
    setAddressTo,
    setKmDistance,
    setDistanceSyncInProgress,
    geocodeAddress,
    setHasLoadedInitialData,
  ]);

  return {
    isFirstRenderAfterLoadRef,
    previousExtraFieldsRef,
    expenseData,
  };
};
